import { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { logger } from '@/lib/logger';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const PWAInstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Vérifier si déjà installé
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const hasSeenBanner = localStorage.getItem('pwa-install-banner-dismissed');
    
    if (isStandalone || hasSeenBanner) {
      return;
    }

    // Détecter iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(iOS);

    // Écouter l'événement beforeinstallprompt (Android/Desktop)
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      // Attendre 3 secondes avant d'afficher la bannière
      setTimeout(() => {
        setShowBanner(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Pour iOS, afficher après 3 secondes si pas déjà installé
    if (iOS && !isStandalone) {
      setTimeout(() => {
        setShowBanner(true);
      }, 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Android/Desktop
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        logger.log('✅ PWA installée');
      }
      
      setDeferredPrompt(null);
      setShowBanner(false);
      localStorage.setItem('pwa-install-banner-dismissed', 'true');
    } else if (isIOS) {
      // iOS - Rediriger vers la page d'instructions
      navigate('/install');
      setShowBanner(false);
      localStorage.setItem('pwa-install-banner-dismissed', 'true');
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-install-banner-dismissed', 'true');
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-in-bottom">
      <div className="bg-gradient-to-r from-primary via-primary-variant to-primary border-t border-white/20 shadow-glow p-4">
        <div className="container mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="bg-white/10 p-2 rounded-lg backdrop-blur-sm">
              <Smartphone className="h-6 w-6 text-white" />
            </div>
            <div className="text-white">
              <h3 className="font-semibold text-sm sm:text-base">
                Installez QQOQ sur votre appareil
              </h3>
              <p className="text-xs sm:text-sm text-white/80">
                {isIOS 
                  ? "Accédez rapidement et utilisez l'app hors ligne"
                  : "Installation rapide - Fonctionne hors ligne"
                }
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              onClick={handleInstallClick}
              size="sm"
              className="bg-white text-primary hover:bg-white/90 font-semibold shadow-lg hover:scale-105 transition-transform"
            >
              <Download className="h-4 w-4 mr-2" />
              Installer
            </Button>
            <Button
              onClick={handleDismiss}
              size="icon"
              variant="ghost"
              className="text-white hover:bg-white/10"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
